// ---------------------------------------------------------------------------
// Platform module — Audit Events log page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

const PAGE_SIZE = 50;

export function AuditEventsPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const actorOptions = t('auditEvents.filters.actorOptions', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<admin.AuditEventSummary>[]>(
    () => [
      {
        key: 'createdAt',
        header: t('auditEvents.columns.timestamp'),
        render: (e) => (
          <span className="text-sm text-ink-muted font-mono whitespace-nowrap">
            {f.date(e.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
          </span>
        ),
      },
      {
        key: 'eventType',
        header: t('auditEvents.columns.eventType'),
        render: (e) => (
          <span className="text-sm font-medium text-ink">{e.eventType}</span>
        ),
      },
      {
        key: 'actor',
        header: t('auditEvents.columns.actor'),
        render: (e) => (
          <span className="text-sm text-ink-muted">
            {e.actorType}
            {e.actorId ? ` (${e.actorId})` : ''}
          </span>
        ),
      },
      {
        key: 'resource',
        header: t('auditEvents.columns.resource'),
        render: (e) => (
          <span className="text-sm text-ink-muted">
            {e.resourceType}:{e.resourceId}
          </span>
        ),
      },
      {
        key: 'action',
        header: t('auditEvents.columns.action'),
        render: (e) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-sunken text-ink">
            {e.action}
          </span>
        ),
      },
    ],
    [f, t],
  );
  const [events, setEvents] = useState<admin.AuditEventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [actorTypeFilter, setActorTypeFilter] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string>('');

  const loadEvents = useCallback((cursor?: string) => {
    setLoading(true);
    setError(null);

    const params: admin.ListAuditEventsParams = { limit: PAGE_SIZE };
    if (eventTypeFilter) params.eventType = eventTypeFilter;
    if (actorTypeFilter) params.actorType = actorTypeFilter;
    if (cursor) params.cursor = cursor;

    api.admin
      .listAuditEvents(params)
      .then((res) => {
        setEvents(res.data);
        setNextCursor(res.page?.nextCursor ?? null);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('auditEvents.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [eventTypeFilter, actorTypeFilter, t]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  if (loading) return <LoadingState rows={6} />;
  if (error) return <ErrorState message={error} onRetry={loadEvents} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('auditEvents.headings.title')}</h1>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div>
          <label
            htmlFor="event-type-filter"
            className="block text-xs font-medium text-ink-muted mb-1"
          >
            {t('auditEvents.filters.eventType')}
          </label>
          <input
            id="event-type-filter"
            type="text"
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            placeholder={t('auditEvents.filters.eventTypePlaceholder')}
            className="block w-52 rounded-sm border border-hairline-strong px-3 py-1.5 text-sm text-ink placeholder-ink-muted focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <label
            htmlFor="actor-type-filter"
            className="block text-xs font-medium text-ink-muted mb-1"
          >
            {t('auditEvents.filters.actorType')}
          </label>
          <select
            id="actor-type-filter"
            value={actorTypeFilter}
            onChange={(e) => setActorTypeFilter(e.target.value)}
            className="block w-48 rounded-sm border border-hairline-strong px-3 py-1.5 text-sm text-ink focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">{actorOptions.all}</option>
            <option value="platform_member">{actorOptions.platform_member}</option>
            <option value="tenant_member">{actorOptions.tenant_member}</option>
            <option value="applicant">{actorOptions.applicant}</option>
            <option value="system">{actorOptions.system}</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={() => {
              setCursorStack([]);
              loadEvents();
            }}
            className="px-4 py-1.5 text-sm font-medium bg-surface-sunken text-ink rounded-sm hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-ink-muted focus:ring-offset-1 transition-colors"
          >
            {t('common.applyFilters')}
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={events}
        rowKey={(e) => e.id}
        emptyMessage={t('auditEvents.empty')}
        ariaLabel={t('auditEvents.ariaLabel')}
        pageSize={PAGE_SIZE}
        nextCursor={nextCursor}
        hasPreviousPage={cursorStack.length > 0}
        onNextPage={(cursor) => {
          setCursorStack((prev) => [...prev, currentCursor]);
          setCurrentCursor(cursor);
          loadEvents(cursor);
        }}
        onPreviousPage={() => {
          const stack = [...cursorStack];
          const previousCursor = stack.pop() ?? '';
          setCursorStack(stack);
          setCurrentCursor(previousCursor);
          loadEvents(previousCursor || undefined);
        }}
      />
    </div>
  );
}
